// src/Dashboard/pages/teacher/Events/components/EventsMobileCards.jsx

import React from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaEnvelope,
  FaInfoCircle,
  FaTrash,
} from "react-icons/fa";

import {
  formatEventDate,
  getEventPhotoSrc,
  isPastEvent,
  parseEventCategories,
} from "../utils/eventsDashboardUtils";

function Switch({ checked, onChange, disabled, tone = "green" }) {
  const tones = {
    green: "bg-emerald-500",
    blue: "bg-blue-600",
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange?.();
      }}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? tones[tone] : "bg-slate-300 dark:bg-slate-700"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export default function EventsMobileCards({
  events = [],
  page,
  setPage,
  totalPages,
  totalItems,
  deletingId,
  togglingIds = {},
  onPreview,
  onEdit,
  onDelete,
  onDetails,
  onGroupMessage,
  onToggleActive,
  onToggleRegistration,
}) {
  return (
    <div className="md:hidden">
      <div className="mb-3 flex items-center justify-between text-sm">
        <span className="text-slate-500 dark:text-slate-400">
          {totalItems} événement{totalItems > 1 ? "s" : ""}
        </span>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaChevronLeft />
          </button>

          <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
            {page}/{totalPages}
          </span>

          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {events.map((event) => {
          const past = isPastEvent(event);
          const imgSrc = getEventPhotoSrc(event);
          const categories = parseEventCategories(event.cate_event);

          return (
            <article
              key={`mobile-${event.id}`}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60"
            >
              <div className="flex gap-3 p-3">
                <img
                  src={imgSrc}
                  alt={event.titre || "Événement"}
                  onClick={() => onPreview(imgSrc)}
                  onError={(imageEvent) => {
                    imageEvent.currentTarget.src = "/img/no-image.jpg";
                  }}
                  className="h-20 w-20 shrink-0 cursor-zoom-in rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold text-slate-900 dark:text-white">
                        {event.titre || "—"}
                      </h3>

                      <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                        {formatEventDate(event)}
                        {past ? " · Passé" : ""}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {event.type_event || "—"} · {event.level_event || "—"}
                      </p>
                    </div>
                  </div>

                  {categories.length ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {categories.map((category) => (
                        <span
                          key={`${event.id}-${category}`}
                          className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-3 grid grid-cols-2 gap-2 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/60">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Public
                      </p>

                      <div className="mt-1">
                        <Switch
                          checked={!!event.is_active}
                          onChange={() => onToggleActive(event)}
                          disabled={!!togglingIds[`active-${event.id}`]}
                        />
                      </div>
                    </div>

                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Inscriptions
                      </p>

                      <div className="mt-1">
                        <Switch
                          checked={Number(event.can_register ?? 1) === 1}
                          onChange={() => onToggleRegistration(event)}
                          disabled={
                            past ||
                            !!togglingIds[`registration-${event.id}`]
                          }
                          tone="blue"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onDetails(event)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700 transition hover:bg-blue-100 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300"
                    >
                      <FaInfoCircle />
                      Détails
                    </button>

                    <button
                      type="button"
                      onClick={() => onGroupMessage(event)}
                      disabled={past}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                    >
                      <FaEnvelope />
                      Groupe
                    </button>

                    <button
                      type="button"
                      onClick={() => onEdit(event)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <FaEdit />
                      Modifier
                    </button>

                    <button
                      type="button"
                      onClick={() => onDelete(event)}
                      disabled={deletingId === event.id}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}